import { media } from "../../design/breakpoints";
/**
 * MotorDetailV2.js
 * Route: /motor/:motor_id/sensor/:sensor_id/:sensor_type
 * Replaces MotorData component for pump/borewell detail screens.
 * Design: Concept 2 — 24hr timeline + Vaaripa green palette.
 */

import axios from "axios";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import { GlobalState } from "../../GlobalState";
import moment from "moment";
import { BiDownload } from "react-icons/bi";
import PullToRefresh from "../utils/PullToRefresh";

/* ─── Palette ───────────────────────────────────────────────────────────────── */
const GREEN      = "#237A35";
const DARK_GREEN = "#0D3318";
const LT_GREEN   = "#F0FDF4";
const GRAY_BG    = "#F4F6F9";

/* ─── Helpers ───────────────────────────────────────────────────────────────── */
function parseDuration(str) {
  if (!str) return 0;
  const h = str.match(/(\d+)\s*hr/);
  const m = str.match(/(\d+)\s*min/);
  return (h ? parseInt(h[1]) : 0) * 60 + (m ? parseInt(m[1]) : 0);
}

function fmtDuration(mins) {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h)      return `${h}h`;
  return `${m}m`;
}

function timeToPercent(timeStr) {
  // "HH:mm:ss" → percentage of 24hr day
  if (!timeStr) return 0;
  const parts = timeStr.split(":").map(Number);
  return ((parts[0] * 60 + parts[1]) / 1440) * 100;
}

function durationToPercent(mins) {
  return (mins / 1440) * 100;
}

/* ─── Styled Components ─────────────────────────────────────────────────────── */
const Screen = styled.div`
  background: ${GRAY_BG};
  min-height: 100vh;
  font-family: 'Inter', -apple-system, sans-serif;
`;

/* Nav */
const Nav = styled.div`
  background: #fff;
  padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  border-bottom: 1px solid #EAECEF;
  position: sticky; top: 0; z-index: 20;
`;

const BackBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%;
  border: 1.5px solid #E5E7EB; background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; cursor: pointer; color: #374151;
  &:hover { border-color: ${GREEN}; color: ${GREEN}; }
`;

const NavInfo = styled.div` flex: 1; min-width: 0; `;
const NavEyebrow = styled.div` font-size: 11px; color: #4B5563; margin-bottom: 2px; `;
const NavTitle = styled.div`
  font-size: 17px; font-weight: 700; color: #111;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const NavRight = styled.div` display: flex; align-items: center; gap: 8px; `;

const ModeBadge = styled.div`
  padding: 5px 11px; border-radius: 20px;
  font-size: 11px; font-weight: 600;
  background: ${LT_GREEN}; color: #166534; border: 1px solid #BBF7D0;
`;


const TabDateLabel = styled.div`
  margin-left: auto;
  display: flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 500; color: #374151;
  background: #F9FAFB; border: 1.5px solid #E5E7EB;
  border-radius: 20px; padding: 5px 12px;
  white-space: nowrap;
`;
const DlBtn = styled.button`
  width: 34px; height: 34px; border-radius: 9px;
  background: ${GREEN}; color: #fff; border: none;
  font-size: 14px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
`;

/* Header card */
const HeaderCard = styled.div`
  background: #fff;
  padding: 16px 18px 14px;
  border-bottom: 1px solid #EAECEF;
`;

/* Borewell settings — compact inline editable runtime & water level */
const InlineField = styled.label`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; color: #4B5563; font-weight: 500; white-space: nowrap;
`;
const InlineInput = styled.input`
  width: 52px; height: 32px; border: 1.5px solid #E5E7EB; border-radius: 8px;
  padding: 0 8px; font-size: 13px; font-weight: 600; color: #111; text-align: center;
  font-family: inherit; background: #F9FAFB; outline: none;
  &:focus { border-color: ${GREEN}; background: #fff; }
`;
const InlineSave = styled.button`
  height: 32px; padding: 0 14px; border-radius: 8px; border: none;
  background: ${({ $dirty }) => $dirty ? GREEN : "#E5E7EB"};
  color: ${({ $dirty }) => $dirty ? "#fff" : "#9CA3AF"};
  font-size: 13px; font-weight: 600; font-family: inherit;
  cursor: ${({ $dirty }) => $dirty ? "pointer" : "default"};
`;
const SaveMsg = styled.span`
  font-size: 12px; font-weight: 600; white-space: nowrap;
  color: ${({ $err }) => $err ? "#DC2626" : GREEN};
`;

const StatusRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-bottom: 14px; flex-wrap: wrap; row-gap: 10px;
`;
/* Edit controls: centred in the status line on desktop; on ≤900px they drop to
   their own full-width row below the status + HP. */
const EditGroup = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 10px;
  flex-wrap: wrap; row-gap: 8px;
  ${media.max900} { order: 3; width: 100%; }
`;

const BigStatus = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 16px; border-radius: 12px;
  background: ${({ $on }) => $on ? "rgba(34,197,94,0.10)" : "#F9FAFB"};
  border: 1.5px solid ${({ $on }) => $on ? "#BBF7D0" : "#E5E7EB"};
`;

const pulse = keyframes`
  0%,100% { transform: scale(1); opacity: 1; }
  50%      { transform: scale(1.15); opacity: .6; }
`;

const StatusDot = styled.div`
  width: 10px; height: 10px; border-radius: 50%;
  background: ${({ $on, $offline }) =>
    $offline ? "#94A3B8" : $on ? "#16A34A" : "#94A3B8"};
  ${({ $on, $offline }) => $on && !$offline && css`
    box-shadow: 0 0 0 4px rgba(22,163,74,.15);
    animation: ${pulse} 2s infinite;
  `}
`;

const BigStatusText = styled.div`
  font-size: 14px; font-weight: 700; color: #374151;
`;

const HpBadge = styled.div`
  padding: 4px 10px; border-radius: 8px;
  background: #EFF6FF; color: #1D4ED8;
  font-size: 11px; font-weight: 600;
  ${media.max900} { order: 2; }
`;

/* 24hr day track */
const DayTrackLabel = styled.div`
  display: flex; justify-content: space-between; margin-bottom: 5px;
`;

const DayTrackTitle = styled.div` font-size: 11px; font-weight: 600; color: #4B5563; `;
const DayTrackMeta  = styled.div` font-size: 10px; color: #4B5563; `;

const DayTrack = styled.div`
  height: 28px; background: #F3F4F6; border-radius: 6px;
  overflow: hidden; position: relative; display: flex;
  &::after {
    content: ''; position: absolute; left: 50%; top: 0;
    width: 1.5px; height: 100%;
    background: rgba(0,0,0,0.12); z-index: 2;
  }
`;

const TrackSegment = styled.div`
  height: 100%; position: absolute; top: 0;
  background: ${GREEN}; border-radius: 2px;
`;

const DayTrackAxis = styled.div`
  display: flex; justify-content: space-between; margin-top: 4px;
`;

const AxisLbl = styled.span`
  font-size: 9px; color: #4B5563;
  &.am { color: #EF4444; }
  &.pm { color: #2563EB; }
`;

/* Tabs */
const TabBar = styled.div`
  display: flex; align-items: center; background: #fff;
  padding: 10px 18px;
  border-bottom: 1px solid #EAECEF;
  gap: 6px;
`;

const Tab = styled.button`
  padding: 5px 11px;
  border-radius: 20px;
  border: 1.5px solid ${({ $active }) => $active ? DARK_GREEN : "#E5E7EB"};
  background: ${({ $active }) => $active ? DARK_GREEN : "#fff"};
  color: ${({ $active }) => $active ? "#fff" : "#6B7280"};
  font-size: 11px;
  font-weight: ${({ $active }) => $active ? 600 : 500};
  font-family: inherit;
  cursor: pointer;
  transition: all .12s;
`;

/* Date picker row */
const DatePickerRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: #fff;
  border-bottom: 1px solid #EAECEF; flex-wrap: wrap;
`;

const RpLabel = styled.span`
  font-size: 11px; font-weight: 600; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase;
`;

const RpInput = styled.input`
  height: 30px; border: 1.5px solid #E5E7EB; border-radius: 8px;
  font-size: 11px; font-family: inherit; color: #374151;
  padding: 0 8px; background: #F9FAFB; cursor: pointer;
  &:focus { border-color: ${GREEN}; background: ${LT_GREEN}; outline: none; }

  ${media.max480} {
    padding: 0 6px;
    min-width: 0;

    /* Shrinks Android's native calendar icon so it looks closer to iOS */
    &::-webkit-calendar-picker-indicator {
      width: 14px;
      height: 14px;
      padding: 0;
      margin-left: 2px;
      opacity: 0.6;
    }
  }
`;

const RpApply = styled.button`
  height: 30px; padding: 0 14px;
  background: ${GREEN}; color: #fff; border: none;
  border-radius: 8px; font-size: 11px; font-weight: 600;
  font-family: inherit; cursor: pointer;
`;

const RpSep = styled.span` font-size: 12px; color: #4B5563; font-weight: 600; `;

/* Range navigator */
const RangeNav = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 18px; background: #F9FAFB; border-bottom: 1px solid #EAECEF;
`;

const NavArrow = styled.button`
  width: 30px; height: 30px; border-radius: 8px;
  border: 1.5px solid #E5E7EB; background: #fff;
  font-size: 14px; cursor: pointer; color: #374151;
  display: flex; align-items: center; justify-content: center;
`;

const RangeLabel = styled.div` text-align: center; `;
const RangeLbl = styled.div` font-size: 13px; font-weight: 600; color: #111; `;
const RangeSub = styled.div` font-size: 10px; color: #4B5563; margin-top: 1px; `;

/* Period summary strip */




/* Combined metrics strip — all 4 stats in one dark green bar */
// const MetricsStrip = styled.div`
//   background: linear-gradient(135deg, ${DARK_GREEN} 0%, #1a5c2a 100%);
//   border-radius: 14px;
//   display: grid;
//   grid-template-columns: repeat(4, 1fr);
//   overflow: hidden;
//   margin: 14px 18px 0;
// `;


const MetricsStrip = styled.div`
  background: linear-gradient(135deg, ${DARK_GREEN} 0%, #1a5c2a 100%);
  border-radius: 14px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  overflow: hidden;
  margin: 14px 18px 0;

  ${media.max480} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const MetricCell = styled.div`
  padding: 13px 14px;
  border-right: 1px solid rgba(255,255,255,0.08);
  display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 10px;
  &:last-child { border-right: none; }
`;

const MetricText = styled.div`
  display: flex; flex-direction: column; gap: 2px; min-width: 0;
`;

const MetricIconWrap = styled.div`
  width: 26px; height: 26px; border-radius: 7px;
  background: rgba(251,191,36,0.15);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;

const MetricVal = styled.div`
  font-size: 18px; font-weight: 800; color: #fff; line-height: 1; letter-spacing: -.4px;
  span { font-size: 10px; font-weight: 400; color: rgba(255,255,255,.5); }
`;

const MetricLbl = styled.div`
  font-size: 9px; font-weight: 600; color: rgba(255,255,255,.45);
  letter-spacing: .06em; text-transform: uppercase;
`;

/* Sessions list */
const SessionsSection = styled.div` padding: 0 18px 8px; `;

const SectionHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 0 8px;
`;

const SectionTitle = styled.div` font-size: 13px; font-weight: 700; color: #111; `;

const SectionBadge = styled.div`
  font-size: 10px; font-weight: 600; color: ${GREEN};
  background: ${LT_GREEN}; padding: 3px 9px; border-radius: 20px;
  border: 1px solid #BBF7D0;
`;

const SessionsList = styled.div` display: flex; flex-direction: column; gap: 8px; `;

const SessionItem = styled.div`
  background: #fff; border-radius: 12px;
  border: 1.5px solid #E5E7EB; padding: 12px 14px;
  display: grid;
  grid-template-columns: 60px 1fr 60px;
  align-items: center; gap: 10px;
`;

const STime = styled.div` font-size: 12px; font-weight: 600; color: #374151; `;
const STimeSub = styled.div` font-size: 10px; color: #4B5563; margin-top: 1px; `;

const SBarTrack = styled.div`
  height: 6px; background: #F3F4F6; border-radius: 3px; margin-bottom: 3px;
`;

const SBarFill = styled.div`
  height: 100%; background: ${GREEN}; border-radius: 3px;
`;

const SBarDur = styled.div`
  font-size: 10px; color: ${GREEN}; font-weight: 600;
`;

/* Multi-day timeline (range) */
const TimelineSection = styled.div` padding: 0 18px 20px; `;

const TlHeaderRow = styled.div`
  display: flex; align-items: center;
  padding: 12px 0 2px;
  border-bottom: 1px solid #F3F4F6; margin-bottom: 2px;
`;

const TlDayCol = styled.div`
  width: 52px; flex-shrink: 0;
  font-size: 9px; font-weight: 700; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase;
`;

const TlBarCol = styled.div` flex: 1; `;

const AmPmBand = styled.div` display: flex; margin-bottom: 3px; `;

const AmPmHalf = styled.div`
  flex: 1; text-align: center;
  font-size: 9px; font-weight: 700; letter-spacing: .06em; padding: 2px 0;
  &.am { color: #EF4444; border-right: 1px solid #E5E7EB; }
  &.pm { color: #2563EB; }
`;

const HourLabels = styled.div`
  display: flex; justify-content: space-between; padding-bottom: 4px;
`;

const Hl = styled.span`
  font-size: 8px; color: #4B5563;
  &.am-lbl { color: #FCA5A5; }
  &.pm-lbl { color: #93C5FD; }
`;

const TlRow = styled.div`
  display: flex; align-items: center;
  margin-bottom: 6px; cursor: pointer;
  &:hover > div:nth-child(2) { opacity: .85; }
`;

const TlDayLabel = styled.div` width: 52px; flex-shrink: 0; `;
const TlDayName = styled.div`
  font-size: 11px; font-weight: 600; color: #374151;
  ${({ $today }) => $today && css`color: ${GREEN};`}
`;

const TlDaySub = styled.div` font-size: 9px; color: #4B5563; margin-top: 1px; `;

const TlBarTrack = styled.div`
  flex: 1; height: 22px;
  background: ${({ $today }) => $today ? LT_GREEN : "#F1F5F9"};
  border-radius: 4px; overflow: hidden; position: relative;
  &::after {
    content: ''; position: absolute; left: 50%; top: 0;
    width: 1px; height: 100%; background: rgba(0,0,0,0.08); z-index: 2;
  }
`;

const TlSegment = styled.div`
  position: absolute; top: 0; height: 100%;
  background: ${GREEN}; border-radius: 2px;
`;

const TlMeta = styled.div`
  width: 56px; flex-shrink: 0; text-align: right; padding-left: 8px;
`;

const TlRuntime = styled.div`
  font-size: 11px; font-weight: 700; color: ${GREEN};
`;

const TlCycles = styled.div` font-size: 9px; color: #4B5563; margin-top: 1px; `;

const Chevron = styled.div`
  width: 16px; flex-shrink: 0; margin-left: 4px;
  color: #4B5563; font-size: 10px;
  display: flex; align-items: center; justify-content: center;
  transition: transform .2s;
  ${({ $open }) => $open && css`transform: rotate(90deg);`}
`;

/* Day detail (expandable) */
const DayDetail = styled.div`
  margin: -2px 0 8px 52px;
  background: #fff; border-radius: 10px;
  border: 1.5px solid #E5E7EB; overflow: hidden;
`;

const DdRow = styled.div`
  display: flex; align-items: center;
  padding: 8px 12px; gap: 10px;
  border-bottom: 1px solid #F3F4F6; font-size: 11px;
  &:last-child { border-bottom: none; }
`;

const DdStart = styled.span`
  font-weight: 600; color: #374151; min-width: 58px;
`;

const DdBar = styled.div`
  flex: 1; height: 5px; background: #F3F4F6; border-radius: 3px;
`;

const DdFill = styled.div`
  height: 100%; background: ${GREEN}; border-radius: 3px;
`;

const DdDur = styled.span`
  font-weight: 700; color: ${GREEN}; min-width: 38px; text-align: right;
`;

const DdEnd = styled.span`
  color: #4B5563; min-width: 58px; text-align: right;
`;

const DdLoading = styled.div`
  padding: 10px 12px; font-size: 11px; color: #4B5563; text-align: center;
`;

const TlLegend = styled.div`
  display: flex; gap: 14px; padding: 4px 0 12px;
`;

const LegendItem = styled.div`
  display: flex; align-items: center; gap: 4px;
  font-size: 10px; color: #4B5563;
`;

const LegendSwatch = styled.div`
  width: 12px; height: 8px; border-radius: 2px;
  background: ${({ $run }) => $run ? GREEN : "#F1F5F9"};
  border: ${({ $run }) => $run ? "none" : "1px solid #E2E8F0"};
`;

/* Energy cost card */







const LoadingBox = styled.div`
  padding: 40px; text-align: center; color: #4B5563; font-size: 14px;
`;

const EmptyBox = styled.div`
  padding: 30px 18px; text-align: center; color: #4B5563; font-size: 13px;
`;

const Spacer = styled.div` height: 20px; `;

/* ─── Main Component ─────────────────────────────────────────────────────────── */
/* ── Metric strip SVG icons ─────────────────────────────────────────────── */
const IconClock = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FCD34D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <polyline points="12 7 12 12 15 15"/>
  </svg>
);

const IconCycle = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FCD34D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/>
    <polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);

const IconLightning = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FCD34D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

const IconWallet = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FCD34D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2"/>
    <line x1="2" y1="10" x2="22" y2="10"/>
  </svg>
);



export default function MotorDetailV2() {
  const { motor_id, sensor_id, sensor_type } = useParams();
  const history = useHistory();
  const state = useContext(GlobalState);

  /* ── Live pump status ── */
  const [pumpData,  setPumpData]  = useState(null);
  const [pumpLoading, setPumpLoading] = useState(true);

  /* ── Motor doc (runtime / water level settings) ── */
  const [motorDoc,       setMotorDoc]       = useState(null);
  const [editRuntime,    setEditRuntime]    = useState("");
  const [editWaterLimit, setEditWaterLimit] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMsg,    setSettingsMsg]    = useState("");
  const [settingsErr,    setSettingsErr]    = useState(false);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState("today");

  /* ── Date state ── */
  const today = moment().format("YYYY-MM-DD");
  const [singleDate, setSingleDate] = useState(moment().subtract(1,"days").format("YYYY-MM-DD"));
  const [rangeFrom,  setRangeFrom]  = useState(moment().subtract(7,"days").format("YYYY-MM-DD"));
  const [rangeTo,    setRangeTo]    = useState(today);

  /* ── Runtime data ── */
  const [runtimeData,  setRuntimeData]  = useState(null);
  const [rangeData,    setRangeData]    = useState(null);
  const [unitRate,     setUnitRate]     = useState(7.35);
  const [dataLoading,  setDataLoading]  = useState(false);

  /* ── Expanded day detail for range ── */
  const [expandedDay, setExpandedDay] = useState(null);
  const [dayDetail,   setDayDetail]   = useState({});   // { "YYYY-MM-DD": periods[] }
  const [dayDetailLoading, setDayDetailLoading] = useState({});

  /* ── Fetch unit rate ── */
  useEffect(() => {
    async function fetchRate() {
      try {
        const org_id = localStorage.getItem("org_id");
        if (!org_id) return;
        const m = moment().format("MM");
        const y = moment().format("YYYY");
        try {
          const r = await axios.get(
            `${process.env.REACT_APP_API}/user/unitrate/monthly/${org_id}/${y}/${m}`,
            { headers: { Authorization: state.token[0] } }
          );
          if (r.data?.data?.current_unit_rate) setUnitRate(r.data.data.current_unit_rate);
        } catch {
          const r = await axios.get(
            `${process.env.REACT_APP_API}/user/unitrate/${org_id}/latest`,
            { headers: { Authorization: state.token[0] } }
          );
          if (r.data?.data?.current_unit_rate) setUnitRate(r.data.data.current_unit_rate);
        }
      } catch { /* use fallback 7.35 */ }
    }
    fetchRate();
  }, [state.token]);

  /* ── Fetch live pump status (polling) ── */
  useEffect(() => {
    async function fetchPump() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/v2/pump_status?motor_id=${motor_id}`,
          { headers: { Authorization: state.token[0] } }
        );
        setPumpData(res.data);
      } catch (e) { console.error("pump_status:", e); }
      finally { setPumpLoading(false); }
    }
    fetchPump();
    const iv = setInterval(fetchPump, 10000);
    return () => clearInterval(iv);
  }, [motor_id, state.token]);

  /* ── Fetch motor doc for runtime / water-level settings ── */
  useEffect(() => {
    async function fetchMotor() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/sensor/motor?sensor_id=${sensor_id}`,
          { headers: { Authorization: state.token[0] } }
        );
        const d = res.data?.data;
        setMotorDoc(d || null);
        if (d) {
          setEditRuntime(d.runtime ?? "");
          setEditWaterLimit(d.water_limit ?? "");
        }
      } catch (e) { console.error("motorGet:", e); }
    }
    if (sensor_id) fetchMotor();
  }, [sensor_id, state.token]);

  /* ── Clamp an integer input to [0, max]; allow empty while typing ── */
  const clampInt = (v, max) => {
    if (v === "") return "";
    const n = Math.floor(Number(v));
    if (isNaN(n)) return "";
    return String(Math.max(0, Math.min(max, n)));
  };

  /* ── Save runtime / water-level back to the motor doc ── */
  const saveSettings = async () => {
    setSavingSettings(true); setSettingsMsg(""); setSettingsErr(false);
    try {
      const body = { runtime: Number(editRuntime), water_limit: Number(editWaterLimit) };
      const res = await axios.patch(
        `${process.env.REACT_APP_API}/sensor/motor?motor_id=${motor_id}`,
        body,
        { headers: { Authorization: state.token[0] } }
      );
      setMotorDoc(res.data?.motor || { ...(motorDoc || {}), ...body });
      setSettingsMsg("Saved");
      setTimeout(() => setSettingsMsg(""), 2500);
    } catch (e) {
      console.error("motorPatch:", e);
      setSettingsErr(true);
      setSettingsMsg("Save failed");
    } finally { setSavingSettings(false); }
  };

  /* ── Fetch runtime data ── */
  const fetchRuntime = useCallback(async (startDate, endDate) => {
    setDataLoading(true);
    setRuntimeData(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/sensor/motor-runtime` +
          `?motor_id=${motor_id}&start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: state.token[0] } }
      );
      setRuntimeData(res.data);
    } catch (e) { console.error("motor-runtime:", e); }
    finally { setDataLoading(false); }
  }, [motor_id, state.token]);

  const fetchRange = useCallback(async (fromDate, toDate) => {
    setDataLoading(true);
    setRangeData(null);
    try {
      const start = moment(fromDate);
      const end   = moment(toDate || rangeTo);
      
      // motor-weekly-runtime returns 7 days from start_date.
      // For longer ranges, batch into weekly chunks and merge.
      const weekStarts = [];
      let cursor = start.clone();
      while (cursor.isSameOrBefore(end, "day")) {
        weekStarts.push(cursor.format("YYYY-MM-DD"));
        cursor.add(7, "days");
      }

      const responses = await Promise.all(
        weekStarts.map(ws =>
          axios.get(
            `${process.env.REACT_APP_API}/sensor/motor-weekly-runtime` +
              `?motor_id=${motor_id}&start_date=${ws}`,
            { headers: { Authorization: state.token[0] } }
          ).then(r => r.data).catch(() => ({ days: [] }))
        )
      );

      // Merge all days, deduplicate by date, filter to selected range
      const allDays = [];
      const seen = new Set();
      responses.forEach(r => {
        (r.days || []).forEach(d => {
          const dateStr = moment(d.date).format("YYYY-MM-DD");
          if (!seen.has(dateStr) &&
              moment(dateStr).isSameOrAfter(start, "day") &&
              moment(dateStr).isSameOrBefore(end, "day")) {
            seen.add(dateStr);
            allDays.push({ ...d, date: dateStr });
          }
        });
      });

      // Sort by date ascending
      allDays.sort((a, b) => a.date.localeCompare(b.date));

      // Use location from first response
      const location = responses.find(r => r.location)?.location || "";

      setRangeData({ days: allDays, location, total_weekly_run_time: "" });
    } catch (e) { console.error("weekly-runtime:", e); }
    finally { setDataLoading(false); }
  }, [motor_id, state.token, rangeTo]);

  /* Initial loads */
  useEffect(() => { fetchRuntime(today, today); }, []);   // eslint-disable-line

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedDay(null);
    if (tab === "today")  fetchRuntime(today, today);
    if (tab === "date")   fetchRuntime(singleDate, singleDate);
    if (tab === "range")  fetchRange(rangeFrom, rangeTo);
  };

  /* ── Fetch a single day's detail for range expand ── */
  const fetchDayDetail = useCallback(async (date) => {
    if (dayDetail[date]) return; // already fetched
    setDayDetailLoading(prev => ({ ...prev, [date]: true }));
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API}/sensor/motor-runtime` +
          `?motor_id=${motor_id}&start_date=${date}&end_date=${date}`,
        { headers: { Authorization: state.token[0] } }
      );
      setDayDetail(prev => ({ ...prev, [date]: res.data.online_periods || [] }));
    } catch { setDayDetail(prev => ({ ...prev, [date]: [] })); }
    finally { setDayDetailLoading(prev => ({ ...prev, [date]: false })); }
  }, [motor_id, state.token, dayDetail]);

  /* ── Derived live status ── */
  const ms        = pumpData?.motor_status || {};
  const isOnline  = pumpData?.sensor_status === "online";
  const isOff     = !ms.motor_on || ms.motor_on === 0 || ms.motor_on === "0";
  const pumpOn    = !isOff;
  const isManual  = ms.manual_mode === 1 || ms.manual_mode === true || ms.manual_mode === "1";
  const locName   = ms.location_name || (sensor_type?.replace(/^Pump - /, "") || "Pump");
  const hp        = ms.capacity_in_hp;
  const isBorewell = !!ms.isBorewell || !!motorDoc?.isBorewell;

  /* ── Settings dirty check (enable Save only on change) ── */
  const settingsValid =
    editRuntime !== "" && editWaterLimit !== "" &&
    !isNaN(Number(editRuntime)) && !isNaN(Number(editWaterLimit));
  const settingsDirty = !!motorDoc && settingsValid && (
    String(editRuntime) !== String(motorDoc.runtime ?? "") ||
    String(editWaterLimit) !== String(motorDoc.water_limit ?? "")
  );

  /* ── Today's 24hr track segments ── */
  const todaySegments = (runtimeData?.online_periods || []).map(p => ({
    left:  timeToPercent(p.start_time),
    width: durationToPercent(parseDuration(p.duration)),
    label: `${moment(p.start_time, "HH:mm:ss").format("h:mm A")} · ${p.duration}`,
  }));

  /* ── Energy for today/date ── */
  const kwh      = parseFloat(runtimeData?.energy_consumed_kwh || 0).toFixed(2);
  const cost     = (parseFloat(kwh) * unitRate).toFixed(2);
  const periods  = runtimeData?.online_periods || [];
  const maxDur   = Math.max(...periods.map(p => parseDuration(p.duration)), 1);

  /* ── Range period totals ── */
  const days = rangeData?.days || [];
  const rangeTotalMins = days.reduce((a, d) => a + parseDuration(d.run_time), 0);
  const rangeTotalKwh  = hp
    ? ((hp * 0.746 * rangeTotalMins) / 60).toFixed(2)
    : "—";
  const rangeTotalCost = hp
    ? (parseFloat(rangeTotalKwh) * unitRate).toFixed(2)
    : "—";
  const rangeCycles = days.reduce((a, d) =>
    a + (parseDuration(d.run_time) > 0 ? 1 : 0), 0);

  /* ── Range navigator shift ── */
  const shiftRange = (dir) => {
    const span = moment(rangeTo).diff(moment(rangeFrom), "days") + 1;
    const newFrom = moment(rangeFrom).add(dir * span, "days").format("YYYY-MM-DD");
    const newTo   = moment(rangeTo).add(dir * span, "days").format("YYYY-MM-DD");
    setRangeFrom(newFrom);
    setRangeTo(newTo);
    fetchRange(newFrom, newTo);
  };

const screenRef = useRef(null);

const handleDownload = async () => {
  try {
    const html2canvas = (await import("html2canvas")).default;
    const target = screenRef.current || document.body;

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#F4F6F9",
      logging: false,
      onclone: (clonedDoc) => {
        const noscripts = clonedDoc.querySelectorAll("noscript");
        noscripts.forEach((el) => el.remove());
      },
      ignoreElements: (el) => {
        const tagName = el.tagName ? el.tagName.toLowerCase() : "";
        return tagName === "noscript" || tagName === "script";
      },
    });

    const dataUrl = canvas.toDataURL("image/png");

    if (navigator.share && navigator.canShare) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "vaaripa-report.png", { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "Vaaripa Report" });
          return;
        }
      } catch (shareErr) {
        console.warn("Share failed, falling back to download:", shareErr);
      }
    }

    const link = document.createElement("a");
    link.download = `vaaripa-report-${Date.now()}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (e) {
    console.error("Screenshot failed:", e);
    alert("Could not generate image: " + e.message);
  }
};

  /* ── Render ── */
  if (pumpLoading) return <LoadingBox>Loading pump data…</LoadingBox>;

  return (
    <Screen>
      <PullToRefresh />
      {/* NAV */}
      <Nav className="no-print" data-ptr-header>
        <BackBtn onClick={() => history.goBack()}>←</BackBtn>
        <NavInfo>
          <NavEyebrow>{isBorewell ? "Borewell" : "Pump"} · Motor detail</NavEyebrow>
          <NavTitle title={locName}>{locName}</NavTitle>
        </NavInfo>
        <NavRight>
          <ModeBadge>{isManual ? "Manual" : "Auto"}</ModeBadge>
          <DlBtn onClick={handleDownload} title="Export PDF">
            <BiDownload size={17} />
          </DlBtn>
        </NavRight>
      </Nav>

      {/* HEADER CARD — status + 24hr day track */}
      <HeaderCard>
        <StatusRow>
          <BigStatus $on={pumpOn && isOnline}>
            <StatusDot $on={pumpOn} $offline={!isOnline} />
            <BigStatusText>
              {!isOnline ? "Offline" : pumpOn ? "ON · Running" : "OFF · Standby"}
            </BigStatusText>
          </BigStatus>

          {isBorewell && motorDoc && (
            <EditGroup className="no-print">
              <InlineField>
                Run time:
                <InlineInput
                  type="number" min="0" max="999"
                  value={editRuntime}
                  onChange={(e) => setEditRuntime(clampInt(e.target.value, 999))}
                />
              </InlineField>
              <InlineField>
                Water level:
                <InlineInput
                  type="number" min="0" max="100"
                  value={editWaterLimit}
                  onChange={(e) => setEditWaterLimit(clampInt(e.target.value, 100))}
                />
              </InlineField>
              <InlineSave
                $dirty={settingsDirty && !savingSettings}
                disabled={!settingsDirty || savingSettings}
                onClick={saveSettings}
              >
                {savingSettings ? "Saving…" : "Save"}
              </InlineSave>
              {settingsMsg && <SaveMsg $err={settingsErr}>{settingsMsg}</SaveMsg>}
            </EditGroup>
          )}

          {hp && <HpBadge>{hp} HP</HpBadge>}
        </StatusRow>

        <DayTrackLabel>
          <DayTrackTitle>Today · Live run timeline</DayTrackTitle>
          <DayTrackMeta>
            {todaySegments.length} {todaySegments.length === 1 ? "cycle" : "cycles"} · {runtimeData?.total_run_time || "0m"}
          </DayTrackMeta>
        </DayTrackLabel>
        <DayTrack>
          {todaySegments.map((s, i) => (
            <TrackSegment key={i} style={{ left: `${s.left}%`, width: `${s.width}%` }} title={s.label} />
          ))}
        </DayTrack>
        <DayTrackAxis>
          <AxisLbl>12a</AxisLbl>
          <AxisLbl className="am">● AM</AxisLbl>
          <AxisLbl>12p</AxisLbl>
          <AxisLbl className="pm">● PM</AxisLbl>
          <AxisLbl>11p</AxisLbl>
        </DayTrackAxis>
      </HeaderCard>

      {/* TABS */}
      <TabBar className="no-print">
        {["today","date","range"].map(t => (
          <Tab key={t} $active={activeTab === t} onClick={() => handleTabChange(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </Tab>
        ))}
        <TabDateLabel>
          {"📅"} {activeTab === "today"
            ? moment().format("D MMM YYYY")
            : activeTab === "date"
            ? moment(singleDate).format("D MMM YYYY")
            : moment(rangeFrom).format("D MMM") + " – " + moment(rangeTo).format("D MMM YYYY")}
        </TabDateLabel>
      </TabBar>

      {/* ══ TODAY ══════════════════════════════════════════════════════════════ */}
      {activeTab === "today" && (
        <>
          <MetricsStrip>
            <MetricCell>
              <MetricIconWrap><IconClock /></MetricIconWrap>
              <MetricText>
              <MetricVal>{fmtDuration(parseDuration(runtimeData?.total_run_time))}</MetricVal>
              <MetricLbl>Total runtime</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconCycle /></MetricIconWrap>
              <MetricText>
              <MetricVal>{periods.length}<span> runs</span></MetricVal>
              <MetricLbl>Cycles today</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconLightning /></MetricIconWrap>
              <MetricText>
              <MetricVal>{kwh}<span> kWh</span></MetricVal>
              <MetricLbl>Energy used</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconWallet /></MetricIconWrap>
              <MetricText>
              <MetricVal>₹ {cost}</MetricVal>
              <MetricLbl>Cost today</MetricLbl>
              </MetricText>
            </MetricCell>
          </MetricsStrip>

          <SessionsSection>
            <SectionHeader>
              <SectionTitle>Run Sessions</SectionTitle>
              <SectionBadge>{moment().format("D MMM")}</SectionBadge>
            </SectionHeader>

            {dataLoading ? (
              <LoadingBox>Loading sessions…</LoadingBox>
            ) : periods.length === 0 ? (
              <EmptyBox>No run sessions today</EmptyBox>
            ) : (
              <SessionsList>
                {periods.map((p, i) => {
                  const dur    = parseDuration(p.duration);
                  const pct    = Math.round((dur / maxDur) * 100);
                  return (
                    <SessionItem key={i}>
                      <div>
                        <STime>{moment(p.start_time,"HH:mm:ss").format("h:mm A")}</STime>
                        <STimeSub>Start</STimeSub>
                      </div>
                      <div>
                        <SBarTrack><SBarFill style={{ width: `${pct}%` }} /></SBarTrack>
                        <SBarDur>{p.duration}</SBarDur>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <STime>{moment(p.end_time,"HH:mm:ss").format("h:mm A")}</STime>
                        <STimeSub>End</STimeSub>
                      </div>
                    </SessionItem>
                  );
                })}
              </SessionsList>
            )}
          </SessionsSection>

          <Spacer />
        </>
      )}

      {/* ══ DATE ══════════════════════════════════════════════════════════════ */}
      {activeTab === "date" && (
        <>
          <DatePickerRow>
            <RpLabel>Date</RpLabel>
            <RpInput
              type="date" value={singleDate} max={today}
              onChange={e => setSingleDate(e.target.value)}
            />
            <RpApply onClick={() => fetchRuntime(singleDate, singleDate)}>Apply</RpApply>
          </DatePickerRow>

          <MetricsStrip>
            <MetricCell>
              <MetricIconWrap><IconClock /></MetricIconWrap>
              <MetricText>
              <MetricVal>{fmtDuration(parseDuration(runtimeData?.total_run_time))}</MetricVal>
              <MetricLbl>Total runtime</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconCycle /></MetricIconWrap>
              <MetricText>
              <MetricVal>{periods.length}<span> runs</span></MetricVal>
              <MetricLbl>Cycles</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconLightning /></MetricIconWrap>
              <MetricText>
              <MetricVal>{kwh}<span> kWh</span></MetricVal>
              <MetricLbl>Energy used</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconWallet /></MetricIconWrap>
              <MetricText>
              <MetricVal>₹ {cost}</MetricVal>
              <MetricLbl>Cost</MetricLbl>
              </MetricText>
            </MetricCell>
          </MetricsStrip>

          <SessionsSection>
            <SectionHeader>
              <SectionTitle>Run Sessions</SectionTitle>
              <SectionBadge>{moment(singleDate).format("D MMM YYYY")}</SectionBadge>
            </SectionHeader>
            {dataLoading ? (
              <LoadingBox>Loading…</LoadingBox>
            ) : periods.length === 0 ? (
              <EmptyBox>No run sessions for {moment(singleDate).format("D MMM YYYY")}</EmptyBox>
            ) : (
              <SessionsList>
                {periods.map((p, i) => {
                  const dur = parseDuration(p.duration);
                  const pct = Math.round((dur / maxDur) * 100);
                  return (
                    <SessionItem key={i}>
                      <div>
                        <STime>{moment(p.start_time,"HH:mm:ss").format("h:mm A")}</STime>
                        <STimeSub>Start</STimeSub>
                      </div>
                      <div>
                        <SBarTrack><SBarFill style={{ width: `${pct}%` }} /></SBarTrack>
                        <SBarDur>{p.duration}</SBarDur>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <STime>{moment(p.end_time,"HH:mm:ss").format("h:mm A")}</STime>
                        <STimeSub>End</STimeSub>
                      </div>
                    </SessionItem>
                  );
                })}
              </SessionsList>
            )}
          </SessionsSection>

          <Spacer />
        </>
      )}

      {/* ══ RANGE ══════════════════════════════════════════════════════════════ */}
      {activeTab === "range" && (
        <>
          <DatePickerRow>
            <RpLabel>From</RpLabel>
            <RpInput type="date" value={rangeFrom} max={today}
              onChange={e => setRangeFrom(e.target.value)} />
            <RpSep>→</RpSep>
            <RpLabel>To</RpLabel>
            <RpInput type="date" value={rangeTo} max={today}
              onChange={e => setRangeTo(e.target.value)} />
            <RpApply onClick={() => fetchRange(rangeFrom, rangeTo)}>Apply</RpApply>
          </DatePickerRow>

          <RangeNav>
            <NavArrow onClick={() => shiftRange(-1)}>‹</NavArrow>
            <RangeLabel>
              <RangeLbl>
                {moment(rangeFrom).format("D MMM")} – {moment(rangeTo).format("D MMM YYYY")}
              </RangeLbl>
              <RangeSub>
                {moment(rangeTo).diff(moment(rangeFrom), "days") + 1} days
              </RangeSub>
            </RangeLabel>
            <NavArrow onClick={() => shiftRange(1)}>›</NavArrow>
          </RangeNav>

          {/* Period summary strip */}
          {/* <PeriodStrip>
            <PsItem>
              <PsVal $green>{fmtDuration(rangeTotalMins)}</PsVal>
              <PsLbl>Total runtime</PsLbl>
            </PsItem>
            <PsItem>
              <PsVal>{rangeCycles}</PsVal>
              <PsLbl>Active days</PsLbl>
            </PsItem>
            <PsItem>
              <PsVal>{hp ? rangeTotalKwh : "—"}</PsVal>
              <PsLbl>kWh used</PsLbl>
            </PsItem>
            <PsItem>
              <PsVal $amber>{hp ? `₹${rangeTotalCost}` : "—"}</PsVal>
              <PsLbl>Total cost</PsLbl>
            </PsItem>
          </PeriodStrip> */}



          {/* Range metrics strip */}
          <MetricsStrip style={{margin: "10px 18px 0"}}>
            <MetricCell>
              <MetricIconWrap><IconClock /></MetricIconWrap>
              <MetricText>
              <MetricVal>{fmtDuration(rangeTotalMins)}</MetricVal>
              <MetricLbl>Total runtime</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconCycle /></MetricIconWrap>
              <MetricText>
              <MetricVal>{rangeCycles}<span> days</span></MetricVal>
              <MetricLbl>Active days</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconLightning /></MetricIconWrap>
              <MetricText>
              <MetricVal>{hp ? rangeTotalKwh : "—"}<span>{hp ? " kWh" : ""}</span></MetricVal>
              <MetricLbl>Energy used</MetricLbl>
              </MetricText>
            </MetricCell>
            <MetricCell>
              <MetricIconWrap><IconWallet /></MetricIconWrap>
              <MetricText>
              <MetricVal>{hp ? `₹ ${rangeTotalCost}` : "—"}</MetricVal>
              <MetricLbl>Period cost</MetricLbl>
              </MetricText>
            </MetricCell>
          </MetricsStrip>

          {/* Multi-day timeline */}
          <TimelineSection>
            <TlHeaderRow>
              <TlDayCol>Day</TlDayCol>
              <TlBarCol>
                <AmPmBand>
                  <AmPmHalf className="am">● AM</AmPmHalf>
                  <AmPmHalf className="pm">● PM</AmPmHalf>
                </AmPmBand>
                <HourLabels>
                  {["12a","3","6","9","12p","3","6","9","11p"].map((l,i) => (
                    <Hl key={i} className={i < 4 ? "am-lbl" : i < 8 ? "pm-lbl" : ""}>{l}</Hl>
                  ))}
                </HourLabels>
              </TlBarCol>
              <div style={{width:56}} />
            </TlHeaderRow>

            <TlLegend>
              <LegendItem><LegendSwatch $run />Running</LegendItem>
              <LegendItem><LegendSwatch />Off / Standby</LegendItem>
            </TlLegend>

            {dataLoading ? (
              <LoadingBox>Loading timeline…</LoadingBox>
            ) : days.length === 0 ? (
              <EmptyBox>No data for this range</EmptyBox>
            ) : days.map(d => {
              const isToday    = d.date === today;
              const mins       = parseDuration(d.run_time);
              const isExpanded = expandedDay === d.date;
              const details    = dayDetail[d.date];

              /* For range timeline: we only have total runtime per day, not session times.
                 Show a representative bar centred in the day (visual approximation).
                 When expanded, fetch real sessions via motor-runtime. */
              const barWidth = (mins / 1440) * 100;
              const barLeft  = Math.max(0, 50 - barWidth / 2); // centred

              const toggleExpand = () => {
                if (isExpanded) {
                  setExpandedDay(null);
                } else {
                  setExpandedDay(d.date);
                  fetchDayDetail(d.date);
                }
              };

              return (
                <React.Fragment key={d.date}>
                  <TlRow onClick={toggleExpand}>
                    <TlDayLabel>
                      <TlDayName $today={isToday}>{moment(d.date).format("D MMM")}</TlDayName>
                      <TlDaySub>
                        {isToday ? "Today" : moment(d.date).format("ddd")}
                      </TlDaySub>
                    </TlDayLabel>

                    <TlBarTrack $today={isToday}>
                      {mins > 0 && (
                        <TlSegment
                          style={{ left: `${barLeft}%`, width: `${barWidth}%` }}
                          title={d.run_time}
                        />
                      )}
                    </TlBarTrack>

                    <TlMeta>
                      <TlRuntime>{fmtDuration(mins)}</TlRuntime>
                      <TlCycles>{mins > 0 ? "tap ›" : "no run"}</TlCycles>
                    </TlMeta>
                    <Chevron $open={isExpanded}>›</Chevron>
                  </TlRow>

                  {isExpanded && (
                    <DayDetail>
                      {dayDetailLoading[d.date] ? (
                        <DdLoading>Loading sessions…</DdLoading>
                      ) : !details || details.length === 0 ? (
                        <DdLoading>No session data</DdLoading>
                      ) : details.map((p, i) => {
                        const dur    = parseDuration(p.duration);
                        const maxD   = Math.max(...details.map(x => parseDuration(x.duration)), 1);
                        const pct    = Math.round((dur / maxD) * 100);
                        return (
                          <DdRow key={i}>
                            <DdStart>{moment(p.start_time,"HH:mm:ss").format("h:mm A")}</DdStart>
                            <DdBar><DdFill style={{ width:`${pct}%` }} /></DdBar>
                            <DdDur>{p.duration}</DdDur>
                            <DdEnd>{moment(p.end_time,"HH:mm:ss").format("h:mm A")}</DdEnd>
                          </DdRow>
                        );
                      })}
                    </DayDetail>
                  )}
                </React.Fragment>
              );
            })}
          </TimelineSection>

          <Spacer />
        </>
      )}
    </Screen>
  );
}